import { useEffect, useState } from 'react';
import api from '../services/api';

/**
 * @module useQuestionTypes
 * @description Custom hook for fetching question types in the Mental Health Portal application.
 * Automatically fetches data when the component mounts and manages loading and error states.
 *
 * @requires react
 * @requires ../services/api
 */

/**
 * Hook to fetch and manage the list of question types
 * @function useQuestionTypes
 * @returns {Object} Object containing questionTypes array, loading state, and error state
 */
export function useQuestionTypes() {
  /**
   * @state {Array} questionTypes - The list of fetched question types
   */
  const [questionTypes, setQuestionTypes] = useState([]);

  /**
   * @state {boolean} loading - Indicates if the API request is in progress
   */
  const [loading, setLoading] = useState(true);

  /**
   * @state {string|null} error - Error message if the API request fails, null otherwise
   */
  const [error, setError] = useState(null);

  /**
   * Effect hook to fetch question types when the component mounts
   * @effect
   */
  useEffect(() => {
    /**
     * Fetches question types from the API
     * @async
     * @function fetchQuestionTypes
     * @returns {Promise<void>}
     */
    async function fetchQuestionTypes() {
      try {
        const { data } = await api.get('question-types');
        setQuestionTypes(data);
      } catch (err) {
        setError('Erro ao buscar tipos de pergunta');
      } finally {
        setLoading(false);
      }
    }

    fetchQuestionTypes();
  }, []);

  /**
   * @typedef {Object} UseQuestionTypesReturn
   * @property {Array} questionTypes - The list of fetched question types
   * @property {boolean} loading - Indicates if the API request is in progress
   * @property {string|null} error - Error message if the API request fails, null otherwise
   */

  /**
   * @returns {UseQuestionTypesReturn} The hook's return values
   */
  return { questionTypes, loading, error };
}
